/**
 * Team Inbox — Create Contact / Create Lead FORM (item 2 — fixes the bug where
 * triage dumped the phone number into BOTH name and phone).
 *
 * A small modal: Name (empty, placeholder "Full name", REQUIRED), Phone
 * (pre-filled from the sender, editable), Email (optional; pre-filled for email
 * triage). On save the typed values flow to createContactFromForm /
 * createLeadFromForm. Reuses the picker/compose styling (ti-modal-backdrop +
 * ti-compose chrome) so it sits inside the ONE-UI system.
 */
import { useState } from 'react';
import { buildCreateFromForm } from '../lib/attachExisting';

export type CreateMode = 'contact' | 'lead';

export default function CreateContactForm({
  mode,
  channel,
  prefillPhone = '',
  prefillEmail = '',
  busy = false,
  onSave,
  onClose,
}: {
  mode: CreateMode;
  channel: 'whatsapp' | 'email';
  /** Pre-filled (editable) phone — the sender's number for WA triage. */
  prefillPhone?: string;
  /** Pre-filled (editable) email — the sender's address for email triage. */
  prefillEmail?: string;
  busy?: boolean;
  /** Save with the TYPED name + editable phone/email. */
  onSave: (form: { name: string; phone: string; email: string }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState(prefillPhone);
  const [email, setEmail] = useState(prefillEmail);
  const [touched, setTouched] = useState(false);

  const result = buildCreateFromForm({ name, phone, email });
  const title = mode === 'lead' ? 'Create lead' : 'Create contact';
  const sub =
    mode === 'lead'
      ? 'New sales lead from this sender. Type the name — the number is no longer copied into it.'
      : 'Adds this sender to the people directory. Type the name — the number is no longer copied into it.';

  function submit() {
    setTouched(true);
    if (!result.valid) return;
    onSave({ name: result.name, phone: result.phone, email: result.email });
  }

  return (
    <div className="ti-modal-backdrop" role="dialog" aria-modal aria-label={title}>
      <div className="ti-compose ti-createform">
        <div className="ti-compose-head">
          <h3>{title}</h3>
          <button
            type="button"
            className="ti-compose-x"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <p className="ti-createform-sub">{sub}</p>

        <label className="ti-createform-label" htmlFor="ti-cf-name">
          {mode === 'lead' ? 'Lead name' : 'Full name'}
        </label>
        <input
          id="ti-cf-name"
          className="ti-createform-input"
          autoFocus
          value={name}
          placeholder="Full name"
          aria-invalid={touched && !result.valid}
          onChange={(e) => setName(e.target.value)}
        />
        {touched && !result.valid && (
          <span className="ti-createform-hint">A name is required.</span>
        )}

        <label className="ti-createform-label" htmlFor="ti-cf-phone">
          Phone{channel === 'email' ? ' (optional)' : ''}
        </label>
        <input
          id="ti-cf-phone"
          className="ti-createform-input"
          value={phone}
          placeholder="+91 …"
          onChange={(e) => setPhone(e.target.value)}
        />

        <label className="ti-createform-label" htmlFor="ti-cf-email">
          Email{channel === 'whatsapp' ? ' (optional)' : ''}
        </label>
        <input
          id="ti-cf-email"
          className="ti-createform-input"
          value={email}
          placeholder="name@email.com"
          onChange={(e) => setEmail(e.target.value)}
        />

        <div className="ti-createform-acts">
          <button type="button" className="ti-cf-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-cf-btn pri"
            onClick={submit}
            disabled={busy || !result.valid}
          >
            {busy ? 'Saving…' : mode === 'lead' ? 'Save lead' : 'Save contact'}
          </button>
        </div>
      </div>
    </div>
  );
}
